import { apiService } from '../api';
import {
  AdmissionCycle,
  AdmissionCycleStats,
  BuiltInFormField,
  AdmissionIntake,
  CycleEditLocks,
  AdmissionCyclesListResponse,
  AdmissionApplication,
  AdmissionApplicationsListResponse,
  SeatSnapshot,
  Pagination,
} from '../../types/admissions';

export interface MeritListResponse {
  items: AdmissionApplication[];
  seatSnapshot: SeatSnapshot | null;
  pagination: Pagination;
}

// Report shapes — mirror backend/src/services/admissions/admissionReports.ts.
export interface FunnelReport {
  total: number;
  byStatus: Record<string, number>;
  conversion: { assessedPct: number; offeredPct: number; acceptedPct: number; enrolledPct: number };
}
export interface SeatUtilizationReport {
  classId: string;
  className?: string;
  totalSeats: number;
  overbookPercent: number;
  hardCap: number;
  offered: number;
  accepting: number;
  enrolled: number;
  waitlisted: number;
  remaining: number;
}
export interface FeeBucket { invoiceCount: number; billed: number; paid: number; outstanding: number }
export interface RevenueReport {
  applicationFees: FeeBucket;
  admissionFees: FeeBucket;
  totalCollected: number;
  totalOutstanding: number;
  totalRefunded: number;
}
export interface LostReasonsReport {
  withdrawn: Array<{ reason: string; count: number }>;
  declined:  Array<{ reason: string; count: number }>;
  rejected:  Array<{ reason: string; count: number }>;
}
export interface TimeToDecisionReport {
  decidedCount: number;
  medianDays: number | null;
  p75Days: number | null;
  p90Days: number | null;
  avgDays: number | null;
}
export interface AdmissionReports {
  funnel: FunnelReport;
  seatUtilization: SeatUtilizationReport[];
  revenue: RevenueReport;
  lostReasons: LostReasonsReport;
  timeToDecision: TimeToDecisionReport;
  generatedAt: string;
}

/**
 * Wraps the /api/admission-cycles REST surface. See
 * backend/src/controllers/admissions/admissionCycleController.ts for the
 * server-side contract.
 *
 * Pagination lives on the backend — pass `page` + `limit`; receive items +
 * pagination envelope. Reads always return the envelope so the caller can
 * render both the rows and the Next/Prev state in one shot.
 */
/** Groups of cycle settings, as on the Settings tab (mirror of the backend's
 *  COPYABLE_SETTINGS). */
export type CycleSettingsPart = 'seats' | 'form' | 'process' | 'fees';

export const admissionCycleService = {
  list: async (params?: {
    status?: string;
    sessionId?: string;
    q?: string;
    page?: number;
    limit?: number;
  }): Promise<AdmissionCyclesListResponse> => {
    const r = await apiService.get<AdmissionCyclesListResponse>('/admission-cycles', params);
    return r.data!;
  },

  get: async (id: string): Promise<AdmissionCycle> => {
    const r = await apiService.get<AdmissionCycle>(`/admission-cycles/${id}`);
    return r.data!;
  },

  create: async (payload: Partial<AdmissionCycle> & { name: string; sessionId: string }): Promise<AdmissionCycle> => {
    const r = await apiService.post<AdmissionCycle>('/admission-cycles', payload);
    return r.data!;
  },

  editLocks: async (id: string): Promise<CycleEditLocks | null> => {
    const r = await apiService.get<CycleEditLocks | null>(`/admission-cycles/${id}/edit-locks`);
    return r.data ?? null;
  },

  update: async (id: string, payload: Partial<AdmissionCycle>): Promise<AdmissionCycle> => {
    const r = await apiService.patch<AdmissionCycle>(`/admission-cycles/${id}`, payload);
    return r.data!;
  },

  remove: async (id: string): Promise<void> => {
    await apiService.delete(`/admission-cycles/${id}`);
  },

  open:    async (id: string) => (await apiService.post<AdmissionCycle>(`/admission-cycles/${id}/open`)).data!,
  close:   async (id: string) => (await apiService.post<AdmissionCycle>(`/admission-cycles/${id}/close`)).data!,
  archive: async (id: string) => (await apiService.post<AdmissionCycle>(`/admission-cycles/${id}/archive`)).data!,

  stats: async (id: string): Promise<AdmissionCycleStats> => {
    const r = await apiService.get<AdmissionCycleStats>(`/admission-cycles/${id}/stats`);
    return r.data!;
  },

  seatMatrix: async (id: string): Promise<SeatSnapshot[]> => {
    const r = await apiService.get<SeatSnapshot[]>(`/admission-cycles/${id}/seat-matrix`);
    return r.data || [];
  },

  meritList: async (id: string, params: { classId: string; page?: number; limit?: number }): Promise<MeritListResponse> => {
    const r = await apiService.get<MeritListResponse>(`/admission-cycles/${id}/merit`, params);
    return r.data!;
  },


  reports: async (id: string): Promise<AdmissionReports> => {
    const r = await apiService.get<AdmissionReports>(`/admission-cycles/${id}/reports`);
    return r.data!;
  },

  /** Copy settings, part by part, from another cycle into this Draft cycle. */
  copyFrom: async (id: string, sourceCycleId: string, parts: CycleSettingsPart[]): Promise<AdmissionCycle> => {
    const r = await apiService.post<AdmissionCycle>(`/admission-cycles/${id}/copy-from`, { sourceCycleId, parts });
    return r.data!;
  },

  /** EduBook's built-in fields, each with the section it belongs in — for the form editor. */
  builtInFields: async (): Promise<BuiltInFormField[]> =>
    (await apiService.get<BuiltInFormField[]>('/admission-cycles/built-in-fields')).data!,

  uploadProspectus: async (id: string, file: File): Promise<AdmissionCycle> => {
    const fd = new FormData();
    fd.append('admissionProspectus', file);
    const r = await apiService.upload<AdmissionCycle>(`/admission-cycles/${id}/prospectus`, fd);
    return r.data!;
  },

  removeProspectus: async (id: string): Promise<AdmissionCycle> => {
    const r = await apiService.delete<AdmissionCycle>(`/admission-cycles/${id}/prospectus`);
    return r.data!;
  },

  waitlist: async (id: string, params?: { classId?: string; page?: number; limit?: number }): Promise<AdmissionApplicationsListResponse> => {
    const r = await apiService.get<AdmissionApplicationsListResponse>(`/admission-cycles/${id}/waitlist`, params);
    return r.data!;
  },

  // Public-token management. Rotate invalidates the previous link.
  regeneratePublicToken: async (id: string): Promise<{ publicToken: string; publicTokenRotatedAt: string }> => {
    const r = await apiService.post<{ publicToken: string; publicTokenRotatedAt: string }>(`/admission-cycles/${id}/public-token/rotate`);
    return r.data!;
  },

  /** Switch new applications on or off — online form, walk-ins, classes — without ending the cycle. */
  setIntake: async (id: string, intake: Partial<AdmissionIntake>): Promise<AdmissionCycle> => {
    const r = await apiService.patch<AdmissionCycle>(`/admission-cycles/${id}/intake`, intake);
    return r.data!;
  },

  /** For FE-side reference — maps a cycle's publicToken to the parent-
   *  facing URL. Matches the backend route mounted at /api/public/apply. */
  publicApplyUrl: (publicToken: string): string =>
    `${window.location.origin}/apply/${publicToken}`,
};

// Shape unchanged. Also expose a named type for consumers.
export type AdmissionCycleServiceShape = typeof admissionCycleService;

// Preserve the `AdmissionApplication` import for consumers that re-export it.
void (null as unknown as AdmissionApplication);
